import { useMemo, useState } from 'react';

import { InviteAppShortcutsCookieCache } from '@atlassian/cross-join-nudges/services/invited-app-shortcuts/cache';
import {
	useInvitedAppShortcuts as useInvitedAppShortcutsPlatform,
	type UseInvitedAppShortcutsResult,
} from '@atlassian/cross-join-nudges/services/invited-app-shortcuts/invited-app-shortcuts';

import { Cookies } from '@confluence/cookies';
import { fg } from '@confluence/feature-gating';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { useExperimentalSiteUserPersonalization } from '@confluence/targeting/entry-points/useExperimentalSiteUserPersonalization.ts';

import { useLicensedProducts } from './useLicensedProducts.tsx';

const targetProductsJira = ['jira'] as const;
const targetProductsEmpty = [];

export const useResolveInvitedAppShortcuts = (): UseInvitedAppShortcutsResult => {
	const { cloudId, userId } = useSessionData();
	const { hasJira } = useLicensedProducts();
	const [cacheNeedsRefresh, setCacheNeedsRefresh] = useState(false);

	const userTraits = useExperimentalSiteUserPersonalization({
		skip: !(fg('twcg-460-personalised-invite-notifications-m2-gate') && cacheNeedsRefresh),
		variables: {
			cloudId,
			userId: userId || '',
		},
	});

	return useInvitedAppShortcutsPlatform(
		useMemo(
			() =>
				fg('twcg-460-personalised-invite-notifications-m2-gate')
					? {
							enabled: true,
							targetProducts: hasJira ? targetProductsJira : targetProductsEmpty,
							sourceProduct: 'confluence',
							cloudId,
							userId,
							traits: userTraits.data?.experimentalSiteUserPersonalization?.attributes ?? null,
							cache: getInvitedAppShortcutsCache(),
							onCacheNeedsRefresh: () => {
								setCacheNeedsRefresh(true);
							},
						}
					: { enabled: false },
			[userTraits, cloudId, hasJira, userId],
		),
	);
};

let cache: InviteAppShortcutsCookieCache | undefined;

export const getInvitedAppShortcutsCache = (): InviteAppShortcutsCookieCache => {
	if (!cache) {
		cache = new InviteAppShortcutsCookieCache({
			setCookie: Cookies.set,
			getCookie: Cookies.get,
		});
	}
	return cache;
};
